import React, { Component } from "react";
import PropTypes from "prop-types";
import SunEditor from "suneditor-react";
import "suneditor/dist/css/suneditor.min.css";

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Autocomplete,
  Card,
  Divider,
  Grid,
  IconButton,
  MenuItem,
  Select,
  TextField,
  Tooltip,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";

import ImagePickerField from "components/ContentForms/ImagePickerField";
import DragList, { moveItem } from "components/ContentForms/DragList";
import {
  ICON_SUGGESTIONS,
  SECTION_TYPES,
  emptySection,
  emptySectionItem,
} from "Helpers/ContentValidation";

// Which fields each section type actually uses. Hiding the rest keeps the form
// honest about what will show up on the website.
const FIELDS_BY_TYPE = {
  // A hero carries a primary button, and any extra buttons as rows.
  hero: { body: false, image: true, button: true, items: true },
  rich_text: { body: true, image: false, button: false, items: false },
  icon_list: { body: false, image: false, button: false, items: true },
  // Banner blocks (Who We Are, the global-presence map) end in a call to action.
  image_text: { body: true, image: true, button: true, items: true },
  logo_grid: { body: false, image: false, button: false, items: true },
  cta: { body: true, image: false, button: true, items: false },
  gallery: { body: false, image: false, button: false, items: true },
};

const ITEM_LABEL_BY_TYPE = {
  icon_list: "Row",
  image_text: "Row",
  logo_grid: "Logo",
  gallery: "Image",
};

const SECTION_LIMIT = 40;
const ITEM_LIMIT = 60;

/**
 * The page builder: an ordered list of typed content blocks, each with an
 * optional list of rows.
 *
 * The parent owns the sections array — this component only reports the next
 * value through `onChange`.
 */
export default class SectionEditor extends Component {
  /* ------------------------------------------------------------- sections */

  updateSection = (index, changes) => {
    const next = [...this.props.sections];
    next[index] = { ...next[index], ...changes };
    this.props.onChange(this.renumber(next));
  };

  addSection = () => {
    const { sections, onChange } = this.props;
    if (sections.length >= SECTION_LIMIT) return;
    onChange(this.renumber([...sections, emptySection(sections.length)]));
  };

  removeSection = (index) => {
    const section = this.props.sections[index];
    const label = section.heading || section.key || `Section ${index + 1}`;

    // Removing a block takes real content off the website, so confirm first.
    if (!window.confirm(`Remove "${label}" from this page?`)) return;

    this.props.onChange(
      this.renumber(this.props.sections.filter((item, i) => i !== index))
    );
  };

  moveSection = (index, direction) => {
    const target = index + direction;
    const { sections, onChange } = this.props;
    if (target < 0 || target >= sections.length) return;

    const next = [...sections];
    [next[index], next[target]] = [next[target], next[index]];
    onChange(this.renumber(next));
  };

  // Drag drops a block at an arbitrary position rather than swapping neighbours.
  reorderSections = (from, to) => {
    const { sections, onChange } = this.props;
    onChange(this.renumber(moveItem(sections, from, to)));
  };

  // sort_order always mirrors the on-screen order, so the API stores what the
  // admin arranged without them having to type numbers.
  renumber = (sections) =>
    sections.map((section, index) => ({
      ...section,
      sort_order: index,
      items: (section.items || []).map((item, itemIndex) => ({
        ...item,
        sort_order: itemIndex,
      })),
    }));

  /* ---------------------------------------------------------------- items */

  updateItem = (sectionIndex, itemIndex, changes) => {
    const section = this.props.sections[sectionIndex];
    const items = [...(section.items || [])];
    items[itemIndex] = { ...items[itemIndex], ...changes };
    this.updateSection(sectionIndex, { items });
  };

  addItem = (sectionIndex) => {
    const section = this.props.sections[sectionIndex];
    const items = [...(section.items || [])];
    if (items.length >= ITEM_LIMIT) return;
    items.push(emptySectionItem(items.length));
    this.updateSection(sectionIndex, { items });
  };

  removeItem = (sectionIndex, itemIndex) => {
    const section = this.props.sections[sectionIndex];
    const items = (section.items || []).filter((item, i) => i !== itemIndex);
    this.updateSection(sectionIndex, { items });
  };

  moveItem = (sectionIndex, itemIndex, direction) => {
    const section = this.props.sections[sectionIndex];
    const items = [...(section.items || [])];
    const target = itemIndex + direction;
    if (target < 0 || target >= items.length) return;

    [items[itemIndex], items[target]] = [items[target], items[itemIndex]];
    this.updateSection(sectionIndex, { items });
  };

  reorderItems = (sectionIndex, from, to) => {
    const section = this.props.sections[sectionIndex];
    this.updateSection(sectionIndex, {
      items: moveItem(section.items || [], from, to),
    });
  };

  /* --------------------------------------------------------------- render */

  renderItems(section, sectionIndex, config) {
    if (!config.items) return null;

    const items = section.items || [];
    const itemLabel = ITEM_LABEL_BY_TYPE[section.type] || "Row";

    return (
      <MDBox mt={2}>
        <Divider />

        <MDTypography variant="button" fontWeight="medium" display="block" mt={1}>
          {itemLabel}s
        </MDTypography>
        <MDTypography variant="caption" color="text" display="block" mb={1}>
          {section.type === "logo_grid" || section.type === "gallery"
            ? "Each entry needs an image; the title is used as its alt text and caption."
            : "Each entry shows as an icon, a title and a short description."}
        </MDTypography>

        {items.length === 0 ? (
          <MDTypography variant="caption" color="text" display="block" mb={1}>
            No {itemLabel.toLowerCase()}s yet.
          </MDTypography>
        ) : null}

        {/* The group is per section, so a row can only ever be dropped back into
            the section it came from — never into the section list around it. */}
        <DragList
          group={`page-items-${sectionIndex}`}
          count={items.length}
          onReorder={(from, to) => this.reorderItems(sectionIndex, from, to)}
        >
          {(itemIndex, drag) => {
            const item = items[itemIndex];

            return (
          <Card
            key={item._uid || `item-${itemIndex}`}
            sx={{ p: 2, mb: 2, backgroundColor: "#f8f9fa" }}
            {...drag.rowProps}
          >
            <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={1}>
              <MDBox display="flex" alignItems="center">
                {drag.handle}
                <MDTypography variant="caption" fontWeight="medium" ml={0.5}>
                  {itemLabel} {itemIndex + 1}
                </MDTypography>
              </MDBox>

              <MDBox>
                {/* Spans are required: a disabled button fires no events, so
                    Tooltip cannot listen to it directly. */}
                <Tooltip title="Move up">
                  <span>
                    <IconButton
                      size="small"
                      disabled={itemIndex === 0}
                      onClick={() => this.moveItem(sectionIndex, itemIndex, -1)}
                    >
                      <ArrowUpwardIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Move down">
                  <span>
                    <IconButton
                      size="small"
                      disabled={itemIndex === items.length - 1}
                      onClick={() => this.moveItem(sectionIndex, itemIndex, 1)}
                    >
                      <ArrowDownwardIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title={`Remove ${itemLabel.toLowerCase()}`}>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => this.removeItem(sectionIndex, itemIndex)}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </MDBox>
            </MDBox>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <Autocomplete
                  freeSolo
                  options={ICON_SUGGESTIONS}
                  value={item.icon || ""}
                  onChange={(event, value) =>
                    this.updateItem(sectionIndex, itemIndex, { icon: value || "" })
                  }
                  onInputChange={(event, value) =>
                    this.updateItem(sectionIndex, itemIndex, { icon: value || "" })
                  }
                  renderInput={(params) => (
                    <TextField {...params} label="Icon (optional)" />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={8}>
                <TextField
                  fullWidth
                  label="Title"
                  value={item.title || ""}
                  onChange={(event) =>
                    this.updateItem(sectionIndex, itemIndex, { title: event.target.value })
                  }
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  label="Description"
                  value={item.description || ""}
                  onChange={(event) =>
                    this.updateItem(sectionIndex, itemIndex, {
                      description: event.target.value,
                    })
                  }
                />
              </Grid>

              <Grid item xs={12} sm={8}>
                <ImagePickerField
                  label="Image (optional)"
                  value={item.image || ""}
                  onChange={(path) =>
                    this.updateItem(sectionIndex, itemIndex, { image: path })
                  }
                  onError={this.props.onError}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Link (optional)"
                  placeholder="/contactus or https://..."
                  value={item.link || ""}
                  onChange={(event) =>
                    this.updateItem(sectionIndex, itemIndex, { link: event.target.value })
                  }
                />
              </Grid>
            </Grid>
          </Card>
            );
          }}
        </DragList>

        <MDButton
          variant="outlined"
          color="info"
          size="small"
          startIcon={<AddIcon />}
          onClick={() => this.addItem(sectionIndex)}
          disabled={items.length >= ITEM_LIMIT}
        >
          Add {itemLabel.toLowerCase()}
        </MDButton>
      </MDBox>
    );
  }

  renderSection(section, index, drag) {
    const { errors, onError } = this.props;
    const config = FIELDS_BY_TYPE[section.type] || FIELDS_BY_TYPE.rich_text;
    const typeLabel =
      SECTION_TYPES.find((type) => type.value === section.type)?.label || section.type;

    const sectionError =
      errors[`section_${index}_empty`] ||
      errors[`section_${index}_type`] ||
      Object.keys(errors)
        .filter((key) => key.startsWith(`section_${index}_item_`))
        .map((key) => errors[key])[0];

    return (
      <Accordion
        key={section._uid || `section-${index}`}
        defaultExpanded={false}
        sx={{ mb: 1, border: sectionError ? "1px solid #F44335" : undefined }}
        {...drag.rowProps}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <MDBox width="100%" display="flex" justifyContent="space-between" alignItems="center">
            <MDBox display="flex" alignItems="center">
              {/* Stops the grip from toggling the accordion when clicked. */}
              <MDBox
                onClick={(event) => event.stopPropagation()}
                display="inline-flex"
                mr={1}
              >
                {drag.handle}
              </MDBox>
            <MDBox>
              <MDTypography variant="button" fontWeight="medium">
                {index + 1}. {section.heading || section.key || "Untitled section"}
              </MDTypography>
              <MDTypography variant="caption" color="text" display="block">
                {typeLabel}
                {section.status === "inactive" ? " · hidden on the website" : ""}
              </MDTypography>
              {sectionError ? (
                <MDTypography variant="caption" color="error" display="block">
                  {sectionError}
                </MDTypography>
              ) : null}
            </MDBox>
            </MDBox>

            {/* Reorder / remove sit in the header so they are reachable while
                the section is collapsed. */}
            <MDBox
              onClick={(event) => event.stopPropagation()}
              onFocus={(event) => event.stopPropagation()}
            >
              <Tooltip title="Move up">
                <span>
                  <IconButton
                    size="small"
                    disabled={index === 0}
                    onClick={() => this.moveSection(index, -1)}
                  >
                    <ArrowUpwardIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Move down">
                <span>
                  <IconButton
                    size="small"
                    disabled={index === this.props.sections.length - 1}
                    onClick={() => this.moveSection(index, 1)}
                  >
                    <ArrowDownwardIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Remove section">
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => this.removeSection(index)}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </MDBox>
          </MDBox>
        </AccordionSummary>

        <AccordionDetails>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <MDTypography variant="caption" fontWeight="medium" display="block" mb={0.5}>
                Section type
              </MDTypography>
              <Select
                fullWidth
                value={section.type}
                onChange={(event) => this.updateSection(index, { type: event.target.value })}
                sx={{ height: "45px" }}
              >
                {SECTION_TYPES.map((type) => (
                  <MenuItem key={type.value} value={type.value}>
                    {type.label}
                  </MenuItem>
                ))}
              </Select>
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Block key (optional)"
                helperText="Stable name the website can target, e.g. commitment"
                value={section.key || ""}
                onChange={(event) => this.updateSection(index, { key: event.target.value })}
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <MDTypography variant="caption" fontWeight="medium" display="block" mb={0.5}>
                Visibility
              </MDTypography>
              <Select
                fullWidth
                value={section.status || "active"}
                onChange={(event) => this.updateSection(index, { status: event.target.value })}
                sx={{ height: "45px" }}
              >
                <MenuItem value="active">Visible</MenuItem>
                <MenuItem value="inactive">Hidden</MenuItem>
              </Select>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Heading"
                value={section.heading || ""}
                onChange={(event) => this.updateSection(index, { heading: event.target.value })}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Sub-heading"
                value={section.subheading || ""}
                onChange={(event) =>
                  this.updateSection(index, { subheading: event.target.value })
                }
              />
            </Grid>

            {config.image ? (
              <Grid item xs={12} sm={8}>
                <ImagePickerField
                  label="Section image"
                  value={section.image || ""}
                  onChange={(path) => this.updateSection(index, { image: path })}
                  onError={onError}
                />
              </Grid>
            ) : null}

            {config.image ? (
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Image alt text"
                  value={section.image_alt || ""}
                  onChange={(event) =>
                    this.updateSection(index, { image_alt: event.target.value })
                  }
                />
              </Grid>
            ) : null}

            {config.button ? (
              <>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Button label"
                    value={section.button_label || ""}
                    onChange={(event) =>
                      this.updateSection(index, { button_label: event.target.value })
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Button link"
                    placeholder="/contactus"
                    value={section.button_link || ""}
                    onChange={(event) =>
                      this.updateSection(index, { button_link: event.target.value })
                    }
                  />
                </Grid>
              </>
            ) : null}

            {config.body ? (
              <Grid item xs={12}>
                <MDTypography variant="caption" fontWeight="medium" display="block" mb={0.5}>
                  Body text
                </MDTypography>
                {/* `defaultValue` rather than `setContents`: the editor keeps its
                    own state, so re-rendering the list on every keystroke does
                    not move the cursor. */}
                <SunEditor
                  defaultValue={section.body || ""}
                  onChange={(content) => this.updateSection(index, { body: content })}
                  setOptions={{
                    height: 160,
                    buttonList: [
                      ["undo", "redo"],
                      ["bold", "underline", "italic"],
                      ["fontColor", "hiliteColor"],
                      ["align", "list"],
                      ["link"],
                      ["removeFormat"],
                    ],
                  }}
                />
              </Grid>
            ) : null}
          </Grid>

          {this.renderItems(section, index, config)}
        </AccordionDetails>
      </Accordion>
    );
  }

  render() {
    const { sections } = this.props;

    return (
      <MDBox>
        <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={1}>
          <MDBox>
            <MDTypography variant="h6">Page sections</MDTypography>
            <MDTypography variant="caption" color="text">
              Blocks render top to bottom in the order below. Drag a block by its
              grip to move it anywhere in the list.
            </MDTypography>
          </MDBox>

          <MDButton
            variant="gradient"
            color="info"
            size="small"
            startIcon={<AddIcon />}
            onClick={this.addSection}
            disabled={sections.length >= SECTION_LIMIT}
          >
            Add section
          </MDButton>
        </MDBox>

        {sections.length === 0 ? (
          <MDTypography variant="button" color="text">
            No sections yet — add one to start building this page.
          </MDTypography>
        ) : (
          <DragList
            group="page-sections"
            count={sections.length}
            onReorder={this.reorderSections}
          >
            {(index, drag) => this.renderSection(sections[index], index, drag)}
          </DragList>
        )}

        {sections.length >= SECTION_LIMIT ? (
          <MDTypography variant="caption" color="text" display="block" mt={1}>
            Maximum of {SECTION_LIMIT} sections reached.
          </MDTypography>
        ) : null}
      </MDBox>
    );
  }
}

SectionEditor.defaultProps = {
  errors: {},
  onError: () => {},
};

SectionEditor.propTypes = {
  sections: PropTypes.arrayOf(PropTypes.object).isRequired,
  onChange: PropTypes.func.isRequired,
  errors: PropTypes.objectOf(PropTypes.string),
  onError: PropTypes.func,
};
